import { CurrencyPipe, NgClass } from '@angular/common'
import { Component, DestroyRef, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { BillingService } from '@app/modules/payments/services/billing.service'
import { OrganizationBillingTransaction, OrganizationBillingTransactionsPage } from '@app/v2/settings/types/organization-settings.type'
import { TranslateModule } from '@ngx-translate/core'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { catchError, of } from 'rxjs'
import { OrganizationService } from '../../../../services/organization.service'

export type BillingTransaction = OrganizationBillingTransaction

@Component({
  selector: 'app-billing-transactions',
  standalone: true,
  imports: [TranslateModule, CurrencyPipe, NgClass, NgxSkeletonLoaderModule],
  templateUrl: './billing-transactions.component.html',
  styleUrl: './billing-transactions.component.scss'
})
export class BillingTransactionsComponent implements OnInit {
  @ViewChild('tableScrollContainer')
  set tableScrollContainerRef(el: ElementRef<HTMLDivElement> | undefined) {
    this._tableScrollEl = el?.nativeElement ?? null
    this._connectLoadMoreObserver()
  }

  @ViewChild('loadMoreTrigger')
  set loadMoreTriggerRef(el: ElementRef<HTMLDivElement> | undefined) {
    this._loadMoreTriggerEl = el?.nativeElement ?? null
    this._connectLoadMoreObserver()
  }

  public readonly transactions = signal<BillingTransaction[]>([])
  public readonly isLoading = signal(true)
  public readonly isLoadingMore = signal(false)
  public readonly hasMore = signal(false)

  public readonly emptyStateImage = 'assets/images/v2/noTransactions.svg'

  private readonly _billingService = inject(BillingService)
  private readonly _organizationService = inject(OrganizationService)
  private readonly _destroyRef = inject(DestroyRef)

  private readonly _transactionsPerPage = 20
  private _nextStartingAfter: string | null = null
  private _tableScrollEl: HTMLElement | null = null
  private _loadMoreTriggerEl: HTMLElement | null = null
  private _loadMoreObserver: IntersectionObserver | null = null
  private _reloadTransactionsTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * Load the first page of wallet transactions on init and refresh after billing reloads.
   */
  ngOnInit(): void {
    this._loadTransactions()

    this._destroyRef.onDestroy(() => {
      this._loadMoreObserver?.disconnect()

      if (this._reloadTransactionsTimer) {
        clearTimeout(this._reloadTransactionsTimer)
      }
    })
  }

  /**
   * Refresh the transactions list, optionally after a delay (silent reload keeps the current table visible).
   * @param {number} [delayMs=0] Delay in milliseconds before refetching
   */
  public refresh(delayMs = 0): void {
    this._scheduleTransactionsReload(delayMs)
  }

  /**
   * Schedule a transactions refetch after a delay, clearing any pending timer first.
   * @param {number} delayMs Delay in milliseconds before the refetch runs
   */
  private _scheduleTransactionsReload(delayMs: number): void {
    if (this._reloadTransactionsTimer) {
      clearTimeout(this._reloadTransactionsTimer)
    }

    this._reloadTransactionsTimer = setTimeout(() => {
      this._reloadTransactionsTimer = null
      this._loadTransactions(true)
    }, delayMs)
  }

  /**
   * Whether the transactions list is empty after the initial load.
   */
  public isEmpty(): boolean {
    return !this.isLoading() && this.transactions().length === 0
  }

  /**
   * Split a transaction timestamp into separate date and time labels for display.
   * @param {string} value Raw date/time string from the API
   */
  public getTransactionDateParts(value: string): { datePart: string; timePart: string } {
    const dateTime = (value ?? '').trim()
    if (!dateTime) {
      return { datePart: '', timePart: '' }
    }

    if (dateTime.includes(',')) {
      const [datePart, ...rest] = dateTime.split(',')
      return { datePart: datePart.trim(), timePart: rest.join(',').trim() }
    }

    const isoMatch = dateTime.match(/^(\d{4}-\d{2}-\d{2})\s+(.+)$/)
    if (isoMatch) {
      return { datePart: isoMatch[1], timePart: isoMatch[2].trim() }
    }

    return { datePart: dateTime, timePart: '' }
  }

  /**
   * TrackBy for transaction rows.
   * @param {number} index Row index
   * @param {BillingTransaction} transaction Wallet transaction row
   */
  public trackByTransactionId(index: number, transaction: BillingTransaction): string {
    return transaction.id || String(index)
  }

  /**
   * Gets skeleton theme colors based on current theme.
   */
  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    return this._organizationService.getSkeletonTheme()
  }

  /**
   * Fetch the next page when the load-more sentinel enters the viewport.
   */
  private _loadMore(): void {
    if (this.isLoading() || this.isLoadingMore() || !this.hasMore() || !this._nextStartingAfter) {
      return
    }

    this.isLoadingMore.set(true)

    this._organizationService
      .getOrganizationBillingTransactions(this._billingService.workspaceId, {
        perPage: this._transactionsPerPage,
        startingAfter: this._nextStartingAfter
      })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((page) => {
        this._appendTransactions(page)
        this.isLoadingMore.set(false)
      })
  }

  /**
   * Load wallet transactions, optionally without showing the initial skeleton.
   * @param {boolean} [silent=false] When true, keep the current list visible while refetching
   */
  private _loadTransactions(silent = false): void {
    if (!silent) {
      this.isLoading.set(true)
    }

    this._nextStartingAfter = null

    this._organizationService
      .getOrganizationBillingTransactions(this._billingService.workspaceId, {
        perPage: this._transactionsPerPage
      })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((page) => {
        this._setTransactions(page)
        this.isLoading.set(false)
        this._connectLoadMoreObserver()
      })
  }

  /**
   * Replace the transaction list from an API page response.
   * @param {OrganizationBillingTransactionsPage | null} page Transactions API page
   */
  private _setTransactions(page: OrganizationBillingTransactionsPage | null): void {
    if (!page) {
      this.transactions.set([])
      this.hasMore.set(false)
      this._nextStartingAfter = null
      return
    }

    this.transactions.set(page.transactions)
    this.hasMore.set(!!page.has_more && !!page.next_starting_after)
    this._nextStartingAfter = page.next_starting_after
  }

  /**
   * Append paginated transactions from an API page response.
   * @param {OrganizationBillingTransactionsPage | null} page Transactions API page
   */
  private _appendTransactions(page: OrganizationBillingTransactionsPage | null): void {
    if (!page?.transactions?.length) {
      this.hasMore.set(false)
      this._nextStartingAfter = null
      return
    }

    this.transactions.update((current) => [...current, ...page.transactions])
    this.hasMore.set(!!page.has_more && !!page.next_starting_after)
    this._nextStartingAfter = page.next_starting_after
    this._connectLoadMoreObserver()
  }

  /**
   * Observe the load-more sentinel inside the scroll container and fetch the next page when visible.
   */
  private _connectLoadMoreObserver(): void {
    this._loadMoreObserver?.disconnect()

    if (!this._loadMoreTriggerEl || !this.hasMore()) {
      return
    }

    this._loadMoreObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          this._loadMore()
        }
      },
      {
        root: this._tableScrollEl,
        rootMargin: '120px',
        threshold: 0.01
      }
    )

    this._loadMoreObserver.observe(this._loadMoreTriggerEl)
  }
}
